// Email Notifications Collection Service
// This queues notifications for processing by Firebase Functions
import {
  collection,
  setDoc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  type EmailNotification,
  type TicketNotificationData,
  type TeamInviteNotificationData,
  generateEmailContent,
  generateIdempotencyKey,
} from '@/types/notifications';
import type { Organization, Ticket, TicketResponse } from '@/types';

const COLLECTION = 'emailNotifications';

interface BrandingInfo {
  name: string;
  primaryColor: string;
}

export const notificationsService = {
  // Queue a notification for sending
  queueNotification: async (
    notification: Omit<EmailNotification, 'id' | 'createdAt' | 'status'>
  ): Promise<string> => {
    // Use idempotency key as document ID for atomic deduplication
    const docId = notification.idempotencyKey;
    const docRef = doc(db, COLLECTION, docId);

    // Check if already exists
    const existing = await getDoc(docRef);
    if (existing.exists()) {
      console.log('Duplicate notification skipped:', docId);
      return docId;
    }

    // Write in Firebase Trigger Email extension format
    // Extension expects: to, message.subject, message.html, message.text
    // Using setDoc with the idempotency key as doc ID prevents race condition duplicates
    await setDoc(docRef, {
      // Fields for Firebase Trigger Email extension
      to: notification.recipientEmail,
      message: {
        subject: notification.subject,
        html: notification.htmlBody,
        text: notification.textBody,
      },
      // Audit/tracking metadata (ignored by extension, used for our tracking)
      type: notification.type,
      recipientEmail: notification.recipientEmail,
      recipientName: notification.recipientName || null,
      organizationId: notification.organizationId,
      organizationName: notification.organizationName,
      projectId: notification.projectId || null,
      entityType: notification.entityType,
      entityId: notification.entityId,
      replyUrl: notification.replyUrl || null,
      idempotencyKey: notification.idempotencyKey,
      createdAt: serverTimestamp(),
    });
    return docId;
  },

  // Queue notification when agent replies to customer
  notifyCustomerOfReply: async (
    ticket: Ticket,
    response: TicketResponse,
    organization: Organization,
    baseUrl: string
  ): Promise<void> => {
    const branding: BrandingInfo = {
      name: organization.branding?.portalTitle || organization.name,
      primaryColor: organization.branding?.primaryColor || '#2563eb',
    };

    const replyUrl = `${baseUrl}/p/${organization.slug}/ticket/${ticket.id}`;
    const portalUrl = `${baseUrl}/app/tickets/${ticket.id}`;

    const data: TicketNotificationData = {
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      ticketSubject: ticket.subject,
      submitterName: ticket.submitterName || '',
      submitterEmail: ticket.submitterEmail,
      agentName: response.authorName,
      replyContent: response.content,
      portalUrl,
      replyUrl,
    };

    const emailContent = generateEmailContent.ticketReplyToCustomer(data, branding);

    await notificationsService.queueNotification({
      type: 'ticket_reply_agent',
      recipientEmail: ticket.submitterEmail,
      recipientName: ticket.submitterName,
      organizationId: organization.id,
      organizationName: organization.name,
      projectId: ticket.projectId,
      entityType: 'ticket',
      entityId: ticket.id,
      subject: emailContent.subject,
      htmlBody: emailContent.html,
      textBody: emailContent.text,
      replyUrl,
      idempotencyKey: generateIdempotencyKey('ticket_reply_agent', response.id),
    });
  },

  // Queue notification when customer replies
  notifyAgentsOfCustomerReply: async (
    ticket: Ticket,
    response: TicketResponse,
    organization: Organization,
    agentEmails: string[],
    baseUrl: string
  ): Promise<void> => {
    const branding: BrandingInfo = {
      name: organization.branding?.portalTitle || organization.name,
      primaryColor: organization.branding?.primaryColor || '#2563eb',
    };

    const portalUrl = `${baseUrl}/app/tickets/${ticket.id}`;

    const data: TicketNotificationData = {
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      ticketSubject: ticket.subject,
      submitterName: ticket.submitterName || '',
      submitterEmail: ticket.submitterEmail,
      replyContent: response.content,
      portalUrl,
      replyUrl: portalUrl,
    };

    const emailContent = generateEmailContent.ticketReplyToAgent(data, branding);

    // Send to all relevant agents
    for (const email of agentEmails) {
      await notificationsService.queueNotification({
        type: 'ticket_reply_customer',
        recipientEmail: email,
        organizationId: organization.id,
        organizationName: organization.name,
        projectId: ticket.projectId,
        entityType: 'ticket',
        entityId: ticket.id,
        subject: emailContent.subject,
        htmlBody: emailContent.html,
        textBody: emailContent.text,
        idempotencyKey: generateIdempotencyKey('ticket_reply_customer', `${response.id}_${email}`),
      });
    }
  },

  // Queue notification for new ticket
  notifyAgentsOfNewTicket: async (
    ticket: Ticket,
    organization: Organization,
    agentEmails: string[],
    baseUrl: string
  ): Promise<void> => {
    const branding: BrandingInfo = {
      name: organization.branding?.portalTitle || organization.name,
      primaryColor: organization.branding?.primaryColor || '#2563eb',
    };

    const portalUrl = `${baseUrl}/app/tickets/${ticket.id}`;

    const data: TicketNotificationData = {
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      ticketSubject: ticket.subject,
      ticketDescription: ticket.description,
      submitterName: ticket.submitterName || '',
      submitterEmail: ticket.submitterEmail,
      portalUrl,
      replyUrl: portalUrl,
    };

    const emailContent = generateEmailContent.newTicketNotification(data, branding);

    for (const email of agentEmails) {
      await notificationsService.queueNotification({
        type: 'ticket_created',
        recipientEmail: email,
        organizationId: organization.id,
        organizationName: organization.name,
        projectId: ticket.projectId,
        entityType: 'ticket',
        entityId: ticket.id,
        subject: emailContent.subject,
        htmlBody: emailContent.html,
        textBody: emailContent.text,
        idempotencyKey: generateIdempotencyKey('ticket_created', `${ticket.id}_${email}`),
      });
    }
  },

  // Queue notification when ticket is resolved
  notifyCustomerOfResolution: async (
    ticket: Ticket,
    organization: Organization,
    baseUrl: string
  ): Promise<void> => {
    const branding: BrandingInfo = {
      name: organization.branding?.portalTitle || organization.name,
      primaryColor: organization.branding?.primaryColor || '#2563eb',
    };

    const replyUrl = `${baseUrl}/p/${organization.slug}/ticket/${ticket.id}`;

    const data: TicketNotificationData = {
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      ticketSubject: ticket.subject,
      submitterName: ticket.submitterName || '',
      submitterEmail: ticket.submitterEmail,
      portalUrl: replyUrl,
      replyUrl,
    };

    const emailContent = generateEmailContent.ticketResolvedNotification(data, branding);

    await notificationsService.queueNotification({
      type: 'ticket_resolved',
      recipientEmail: ticket.submitterEmail,
      recipientName: ticket.submitterName,
      organizationId: organization.id,
      organizationName: organization.name,
      projectId: ticket.projectId,
      entityType: 'ticket',
      entityId: ticket.id,
      subject: emailContent.subject,
      htmlBody: emailContent.html,
      textBody: emailContent.text,
      replyUrl,
      idempotencyKey: generateIdempotencyKey('ticket_resolved', ticket.id),
    });
  },

  // Queue team invite notification
  notifyTeamInvite: async (
    email: string,
    inviterName: string,
    organization: Organization,
    role: string,
    inviteUrl: string
  ): Promise<void> => {
    const branding: BrandingInfo = {
      name: 'SupportSpace',
      primaryColor: '#2563eb',
    };

    const data: TeamInviteNotificationData = {
      inviterName,
      organizationName: organization.name,
      role,
      inviteUrl,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
    };

    const emailContent = generateEmailContent.teamInviteNotification(data, branding);

    await notificationsService.queueNotification({
      type: 'team_invite',
      recipientEmail: email,
      organizationId: organization.id,
      organizationName: organization.name,
      entityType: 'member',
      entityId: email,
      subject: emailContent.subject,
      htmlBody: emailContent.html,
      textBody: emailContent.text,
      idempotencyKey: generateIdempotencyKey('team_invite', `${organization.id}_${email}`),
    });
  },

  // Get pending notifications (for Firebase Functions to process)
  getPendingNotifications: async (batchSize = 50): Promise<EmailNotification[]> => {
    const q = query(
      collection(db, COLLECTION),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc'),
      limit(batchSize)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as EmailNotification);
  },

  // Mark notification as sent
  markAsSent: async (notificationId: string): Promise<void> => {
    await updateDoc(doc(db, COLLECTION, notificationId), {
      status: 'sent',
      sentAt: serverTimestamp(),
    });
  },

  // Mark notification as failed
  markAsFailed: async (notificationId: string, error: string): Promise<void> => {
    await updateDoc(doc(db, COLLECTION, notificationId), {
      status: 'failed',
      error,
    });
  },
};
